import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "../../components/Banner";
import AnonymousLimitDialog from "../../components/AnonymousLimitDialog";
import LimitReachedDialog from "../../components/LimitReachedDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useMessage } from "../../components/MessageProvider";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../contexts/AuthContext";
import { useUserSettings } from "../../contexts/UserSettingsContext";
import { clearAnonymousSession, getAnonymousSession } from "../../lib/anonymousSession";
import type { TAccountRecord } from "../../lib/apiClient";
import type { SheetAccountEntry, SheetDocumentData } from "../../types/sheetDocument";
import TAccountWidget from "./components/TAccountWidget";
import SheetCommandEntry from "./components/SheetCommandEntry";
import SheetAccountDialog from "./components/SheetAccountDialog";
import SheetAccountsDrawer from "./components/SheetAccountsDrawer";
import SheetRelatedTotalsPanel from "./components/SheetRelatedTotalsPanel";
import SheetTopToolbar from "./components/SheetTopToolbar";
import EntryListTable, { type EntryListTableRow } from "./components/EntryListTable";
import useSheetCommandPrefill from "./hooks/useSheetCommandPrefill";
import useSheetDragAndDrop from "./hooks/useSheetDragAndDrop";
import useSheetHistory, { type SheetSnapshot, type SheetHistorySnapshot } from "./hooks/useSheetHistory";
import useSheetSuggestions from "./hooks/useSheetSuggestions";
import { useAtomicSheetCommandProcessor } from "./hooks/useAtomicSheetCommandProcessor";
import { appScrollbarSx } from "../../lib/scrollbarSx";
import {
  buildEntryListRows,
  buildWidgetEntries as buildWidgetEntriesModel,
  computeDisplayLetterBySequence,
  computeImbalancedLetters,
  computeImbalancedRelatedSubtotals,
  computeRelatedSubtotals,
} from "./sheetModel";
import type {
  RelatedSubtotalRow,
  TAccountCatalogItem,
  TAccountEditorState,
} from "./types";
import { resolveEntryListComment, resolveEntryListCommentParts } from "./commentUtils";
import { parseSheetCommandBatch } from "./commandParser";
import {
  exportSheetAsPDF,
  exportSheetAsPNG,
  exportSheetAsXLSX,
  getExportBaseFileName,
} from "./exportUtils";
import { trackEvent } from "../../lib/analytics";
import { NARROW_PORTRAIT_MEDIA_QUERY } from "../../lib/responsive";
import { bindExampleReferences, exampleEntries, type ExampleEntry } from "./exampleEntries";

type TAccountEntry = SheetAccountEntry;

const COMMAND_AUTOCOMPLETE_SAFE_BOTTOM_GAP = "clamp(180px, 30vh, 320px)";
export const DEMO_TYPING_DELAY_MS = 25;

interface ActiveExample {
  documentId: string;
  entry: ExampleEntry;
  stepIndex: number;
  baselineSnapshot: SheetSnapshot;
  baselineHistory: SheetHistorySnapshot;
}

const createProvisionalTAccountId = (): string => {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
  }

  throw new Error("Secure UUID generation is unavailable in this browser.");
};


function SheetEditor() {
  const { id: documentId } = useParams<{ id: string }>();
  const currentDocumentIdRef = useRef(documentId);
  const documentGenerationRef = useRef(0);
  if (currentDocumentIdRef.current !== documentId) documentGenerationRef.current += 1;
  currentDocumentIdRef.current = documentId;
  const navigate = useNavigate();
  const nextAutoLetterIndexRef = useRef(0);
  const nextEntrySequenceRef = useRef(1);
  const tAccountsRef = useRef<TAccountEntry[]>([]);
  const uberCommentsByLetterRef = useRef<Record<string, string>>({});
  const forceTwoDecimalDisplayRef = useRef(false);
  const documentTitleRef = useRef("");
  const loadedDocumentIdRef = useRef<string | null>(null);
  const hasTrackedEditEventRef = useRef(false);
  const skipNextDocumentSaveRef = useRef(false);
  const demoTransitionPendingRef = useRef(false);
  const demoTypingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { showError, clearError } = useMessage();
  const {
    loadDocument,
    saveDocument,
    getTAccounts,
    createTAccount,
    updateTAccount,
    deleteTAccount,
    getBillingStatus,
  } = useApi();
  const { user = null } = useAuth() || {};
  const { settings: userSettings } = useUserSettings();
  const session = useMemo(() => getAnonymousSession(), []);
  const sessionAccountId = session?.accountId ?? "00000000-0000-0000-0000-000000000000";
  const isMacLike = useMemo(() => {
    if (typeof navigator === "undefined") {
      return false;
    }

    return /Mac|iPhone|iPad|iPod/u.test(navigator.platform);
  }, []);
  const undoTooltipLabel = isMacLike ? "Undo (⌘Z)" : "Undo (Ctrl+Z)";
  const redoTooltipLabel = isMacLike ? "Redo (⌘⇧Z)" : "Redo (Ctrl+Y / Ctrl+Shift+Z)";
  const [tAccounts, setTAccounts] = useState<TAccountEntry[]>([]);
  const [taccountCatalog, setTaccountCatalog] = useState<TAccountCatalogItem[]>([]);
  const [isAccountsDrawerOpen, setIsAccountsDrawerOpen] = useState(false);
  const [hoveredCatalogTAccountId, setHoveredCatalogTAccountId] = useState<string | null>(null);
  const [taccountEditorState, setTaccountEditorState] = useState<TAccountEditorState | null>(null);
  const [deletePendingTAccount, setDeletePendingTAccount] = useState<TAccountCatalogItem | null>(null);
  const [anonymousLimitDialogOpen, setAnonymousLimitDialogOpen] = useState(false);
  const [planLimitDialogOpen, setPlanLimitDialogOpen] = useState(false);
  const [planLimitMessage, setPlanLimitMessage] = useState("");
  const [exampleSpaceNeeded, setExampleSpaceNeeded] = useState<number | null>(null);
  const [maxTAccountsPerDocument, setMaxTAccountsPerDocument] = useState<number | null>(null);
  const [maxTransactionsPerDocument, setMaxTransactionsPerDocument] = useState<number | null>(null);
  const [uberCommentsByLetter, setUberCommentsByLetter] = useState<Record<string, string>>({});
  const [forceTwoDecimalDisplay, setForceTwoDecimalDisplay] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("");
  const [executedCommandCount, setExecutedCommandCount] = useState(0);
  const [hoveredImbalancedLetter, setHoveredImbalancedLetter] = useState<string | null>(null);
  const exportAreaRef = useRef<HTMLDivElement | null>(null);
  const taccountCatalogRef = useRef<TAccountCatalogItem[]>([]);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [exportFormatNumbersWithCommasOverride, setExportFormatNumbersWithCommasOverride] = useState<boolean | null>(null);
  const [isReadOnlyDocument, setIsReadOnlyDocument] = useState(true);
  const [activeExample, setActiveExample] = useState<ActiveExample | null>(null);
  const [demoAttentionSequence, setDemoAttentionSequence] = useState(0);
  const demoDialogRef = useRef<HTMLDivElement>(null);
  const isExportMenuOpen = Boolean(exportMenuAnchorEl);
  const activeFormatNumbersWithCommas = exportFormatNumbersWithCommasOverride ?? userSettings.ui.formatNumbersWithCommas;

  const incrementExecutedCommandCount = useCallback(() => {
    setExecutedCommandCount((previousCount) => previousCount + 1);
  }, []);

  const handleTAccountVisualizationLimitReached = useCallback((limit: number) => {
    if (!user) {
      setAnonymousLimitDialogOpen(true);
      return;
    }

    setPlanLimitMessage(`You have reached the maximum of ${limit} T-Account visualizations for your account.`);
    setPlanLimitDialogOpen(true);
  }, [user]);

  const handleTransactionLimitReached = useCallback((limit: number) => {
    if (!user) {
      setAnonymousLimitDialogOpen(true);
      return;
    }

    setPlanLimitMessage(`You have reached the maximum of ${limit} transactions for your account.`);
    setPlanLimitDialogOpen(true);
  }, [user]);

  const { prepareEntrySubmit, commitEntrySubmit } = useAtomicSheetCommandProcessor({
    nextAutoLetterIndexRef,
    nextEntrySequenceRef,
    tAccountsRef,
    uberCommentsByLetterRef,
    forceTwoDecimalDisplayRef,
    documentTitleRef,
    setTAccounts,
    setUberCommentsByLetter,
    setForceTwoDecimalDisplay,
    setDocumentTitle,
    setExecutedCommandCount,
    taccountCatalogRef,
    maxTAccountsPerDocument,
    maxTransactionsPerDocument,
    onTAccountVisualizationLimitReached: handleTAccountVisualizationLimitReached,
    onTransactionLimitReached: handleTransactionLimitReached,
    onHelpCommand: () => navigate("/prompt-commands"),
    showError,
    clearError,
  });

  useEffect(() => {
    let active = true;

    const loadPlanLimits = async () => {
      if (!user) {
        setMaxTAccountsPerDocument(3);
        setMaxTransactionsPerDocument(10);
        return;
      }

      const result = await getBillingStatus(
        {
          anonymous_user_id: session?.userId,
          anonymous_account_id: session?.accountId,
        },
        {
          shouldShowError: () => false,
        },
      );

      if (!active) {
        return;
      }

      if (result.ok) {
        setMaxTAccountsPerDocument(result.data.limits.max_taccounts_per_document);
        setMaxTransactionsPerDocument(result.data.limits.max_transactions_per_document);
        return;
      }

      setMaxTAccountsPerDocument(null);
      setMaxTransactionsPerDocument(null);
    };

    void loadPlanLimits();

    return () => {
      active = false;
    };
  }, [getBillingStatus, session?.accountId, session?.userId, user]);

  const persistHistorySnapshot = useCallback(async (snapshot: SheetSnapshot): Promise<boolean> => {
    if (!documentId || isReadOnlyDocument || currentDocumentIdRef.current !== documentId
      || loadedDocumentIdRef.current !== documentId) return false;
    const documentGeneration = documentGenerationRef.current;

    const currentIds = new Set(taccountCatalogRef.current.map((item) => item.taccountId));
    const targetIds = new Set(snapshot.taccountCatalog.map((item) => item.taccountId));
    const referencedTargetIds = new Set(snapshot.tAccounts.map((account) => account.taccountId));
    const newTAccounts = snapshot.taccountCatalog.filter((item) => (
      !currentIds.has(item.taccountId) && referencedTargetIds.has(item.taccountId)
    ));
    const deletedTAccountIds = taccountCatalogRef.current
      .filter((item) => !targetIds.has(item.taccountId))
      .map((item) => item.taccountId);
    const result = await saveDocument({
      document_id: documentId,
      account_id: session?.accountId ?? "00000000-0000-0000-0000-000000000000",
      data: {
        tAccounts: snapshot.tAccounts,
        uberCommentsByLetter: snapshot.uberCommentsByLetter,
        forceTwoDecimalDisplay: snapshot.forceTwoDecimalDisplay,
        documentTitle: snapshot.documentTitle,
      },
      new_taccounts: newTAccounts.map((item) => ({
        taccount_id: item.taccountId,
        name: item.name,
        alias: item.alias ?? undefined,
      })),
      deleted_taccount_ids: deletedTAccountIds,
      anonymous_user_id: user && session?.userId ? session.userId : undefined,
      anonymous_account_id: user && session?.accountId ? session.accountId : undefined,
    });
    if (!result.ok || documentGenerationRef.current !== documentGeneration) return false;
    skipNextDocumentSaveRef.current = true;
    return true;
  }, [documentId, isReadOnlyDocument, saveDocument, session, user]);

  const {
    beginTransition,
    endTransition,
    isTransitionPending,
    undoStack,
    redoStack,
    buildSnapshot,
    pushSnapshot,
    clearHistory,
    captureHistory,
    undo,
    restoreSnapshot,
    handleUndo,
    handleRedo,
  } = useSheetHistory({
    tAccountsRef,
    uberCommentsByLetterRef,
    forceTwoDecimalDisplayRef,
    documentTitleRef,
    nextAutoLetterIndexRef,
    nextEntrySequenceRef,
    taccountCatalogRef,
    setTAccounts,
    setUberCommentsByLetter,
    setForceTwoDecimalDisplay,
    setDocumentTitle,
    setTAccountCatalog: setTaccountCatalog,
    onBeforeApplySnapshot: persistHistorySnapshot,
    onUndoRedoApplied: incrementExecutedCommandCount,
  });

  const formatAmount = useCallback((value: number): string => {
    if (activeFormatNumbersWithCommas) {
      if (userSettings.ui.showDecimals) {
        return value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 20,
      });
    }

    return userSettings.ui.showDecimals ? value.toFixed(2) : String(value);
  }, [activeFormatNumbersWithCommas, userSettings.ui.showDecimals]);

  const formatSignedAmount = useCallback((value: number): string => {
    if (activeFormatNumbersWithCommas) {
      if (userSettings.ui.showDecimals) {
        return value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 20,
      });
    }

    return userSettings.ui.showDecimals ? value.toFixed(2) : String(value);
  }, [activeFormatNumbersWithCommas, userSettings.ui.showDecimals]);

  const {
    commandPrefillRequest,
    prefillCommand,
    handleAmountEditRequest,
    handleCommentEditRequest,
    handleBeginningBalanceEditRequest,
    handleTopLevelCommentRequest,
    handleNewCommentRequest,
    handleTitleEditRequest,
  } = useSheetCommandPrefill({
    formatAmount,
    formatSignedAmount,
    documentTitle,
  });

  const mapTAccountRecord = useCallback((record: TAccountRecord): TAccountCatalogItem => ({
    taccountId: record.taccount_id,
    name: record.name,
    alias: record.alias ?? undefined,
  }), []);

  const openCreateTAccountDialog = useCallback(() => {
    if (isReadOnlyDocument) {
      return;
    }

    setTaccountEditorState({
      mode: "create",
      name: "",
      alias: "",
    });
  }, [isReadOnlyDocument]);

  const openEditTAccountDialog = useCallback((item: TAccountCatalogItem) => {
    if (isReadOnlyDocument) {
      return;
    }

    setTaccountEditorState({
      mode: "edit",
      taccountId: item.taccountId,
      name: item.name,
      alias: item.alias ?? "",
    });
  }, [isReadOnlyDocument]);

  const closeTAccountDialog = useCallback(() => {
    setTaccountEditorState(null);
  }, []);

  const syncSheetAccountLabels = useCallback((catalog: TAccountCatalogItem[]) => {
    setTAccounts((previousAccounts) => previousAccounts.map((entry) => {
      const catalogItem = catalog.find((item) => item.taccountId === entry.taccountId);
      if (!catalogItem) {
        return entry;
      }

      return {
        ...entry,
        account: catalogItem.name,
        alias: catalogItem.alias,
      };
    }));
  }, []);

  const setCatalogState = useCallback((catalog: TAccountCatalogItem[]) => {
    taccountCatalogRef.current = catalog;
    setTaccountCatalog(catalog);
  }, []);

  const handleSaveTAccount = useCallback(async () => {
    if (isReadOnlyDocument) {
      return;
    }

    if (!taccountEditorState) {
      return;
    }

    const name = taccountEditorState.name.trim();
    const alias = taccountEditorState.alias.trim();
    if (!name) {
      showError("Account name is required.");
      return;
    }

    const aliasValue = alias || undefined;

    const result = taccountEditorState.mode === "create"
      ? await createTAccount({ account_id: sessionAccountId, name, alias: aliasValue })
      : await updateTAccount({
        account_id: sessionAccountId,
        taccount_id: taccountEditorState.taccountId ?? "",
        name,
        alias: aliasValue,
      });

    if (!result.ok) {
      return;
    }

    const savedItem = mapTAccountRecord(result.data);
    setTaccountCatalog((previousCatalog) => {
      if (taccountEditorState.mode === "create") {
        const nextCatalog = [...previousCatalog, savedItem]
          .sort((left, right) => left.name.localeCompare(right.name));
        taccountCatalogRef.current = nextCatalog;
        syncSheetAccountLabels(nextCatalog);
        return nextCatalog;
      }

      const nextCatalog = previousCatalog
        .map((item) => (item.taccountId === savedItem.taccountId ? savedItem : item))
        .sort((left, right) => left.name.localeCompare(right.name));
      taccountCatalogRef.current = nextCatalog;
      syncSheetAccountLabels(nextCatalog);
      return nextCatalog;
    });
    setTaccountEditorState(null);
  }, [createTAccount, isReadOnlyDocument, mapTAccountRecord, sessionAccountId, showError, syncSheetAccountLabels, taccountEditorState, updateTAccount]);

  const handleDeleteTAccountConfirmed = useCallback(async () => {
    if (isReadOnlyDocument) {
      return;
    }

    if (!deletePendingTAccount) {
      return;
    }

    const result = await deleteTAccount(deletePendingTAccount.taccountId, sessionAccountId);
    if (!result.ok) {
      return;
    }

    setTaccountCatalog((previousCatalog) => {
      const nextCatalog = previousCatalog.filter((item) => item.taccountId !== deletePendingTAccount.taccountId);
      taccountCatalogRef.current = nextCatalog;
      return nextCatalog;
    });
    setTAccounts((previousAccounts) => previousAccounts.filter((entry) => entry.taccountId !== deletePendingTAccount.taccountId));
    setDeletePendingTAccount(null);
    setExecutedCommandCount((previousCount) => previousCount + 1);
  }, [deletePendingTAccount, deleteTAccount, isReadOnlyDocument, sessionAccountId]);


  const handleLineItemLabelHover = useCallback((letter: string | null) => {
    setHoveredImbalancedLetter(letter);
  }, []);

  const relatedSubtotals = useMemo<RelatedSubtotalRow[]>(() => {
    return computeRelatedSubtotals(tAccounts);
  }, [tAccounts]);

  const imbalancedRelatedSubtotals = useMemo(
    () => computeImbalancedRelatedSubtotals(relatedSubtotals),
    [relatedSubtotals],
  );

  const imbalancedLetters = useMemo(
    () => computeImbalancedLetters(imbalancedRelatedSubtotals),
    [imbalancedRelatedSubtotals],
  );

  const displayLetterBySequence = useMemo(() => {
    return computeDisplayLetterBySequence(tAccounts);
  }, [tAccounts]);

  const shouldShowRelatedTotalsPanel = tAccounts.length > 0 && imbalancedRelatedSubtotals.length > 0;

  const {
    accountSuggestions,
    lineItemReferenceSuggestions,
    commentLineItemReferenceSuggestions,
    editLineItemReferenceSuggestions,
    commentReferenceHelpByReference,
  } = useSheetSuggestions({
    tAccounts,
    taccountCatalog,
    displayLetterBySequence,
    uberCommentsByLetter,
  });

  const buildWidgetEntries = useCallback((account: TAccountEntry) => {
    return buildWidgetEntriesModel(account, displayLetterBySequence, imbalancedLetters);
  }, [displayLetterBySequence, imbalancedLetters]);

  const {
    tAccountLayoutRef,
    registerTAccountRef,
    handleGripMouseDown,
    handleLineItemDragStart,
    draggedAccountId,
    dragPreview,
    lineItemDrag,
    lineItemDropTarget,
    insertionMarkerStyle,
    draggedAccount,
  } = useSheetDragAndDrop({
    tAccounts,
    setTAccounts,
    buildSnapshot,
    pushSnapshot,
    onMutationApplied: incrementExecutedCommandCount,
  });

  const handleAmountEditRequestIfWritable = isReadOnlyDocument ? undefined : handleAmountEditRequest;
  const handleBeginningBalanceEditRequestIfWritable = isReadOnlyDocument ? undefined : handleBeginningBalanceEditRequest;
  const handleCommentEditRequestIfWritable = isReadOnlyDocument ? undefined : handleCommentEditRequest;
  const handleTopLevelCommentRequestIfWritable = isReadOnlyDocument ? undefined : handleTopLevelCommentRequest;
  const handleNewCommentRequestIfWritable = isReadOnlyDocument ? undefined : handleNewCommentRequest;
  const handleTitleEditRequestIfWritable = isReadOnlyDocument ? undefined : handleTitleEditRequest;

  useEffect(() => {
    tAccountsRef.current = tAccounts;
  }, [tAccounts]);

  useEffect(() => {
    uberCommentsByLetterRef.current = uberCommentsByLetter;
  }, [uberCommentsByLetter]);

  useEffect(() => {
    forceTwoDecimalDisplayRef.current = forceTwoDecimalDisplay;
  }, [forceTwoDecimalDisplay]);

  useEffect(() => {
    documentTitleRef.current = documentTitle;
  }, [documentTitle]);

  const executeEntrySubmitWithHistory = useCallback(async (value: string): Promise<boolean> => {
    if (!documentId || currentDocumentIdRef.current !== documentId
      || loadedDocumentIdRef.current !== documentId) return false;
    const documentGeneration = documentGenerationRef.current;
    const previousSnapshot = buildSnapshot();
    let parsedCommands;

    try {
      parsedCommands = parseSheetCommandBatch(value);
    } catch {
      prepareEntrySubmit(value);
      return false;
    }

    const provisionalAccounts: TAccountCatalogItem[] = [];
    const stagedCatalog = taccountCatalogRef.current.map((item) => ({ ...item }));
    parsedCommands.forEach((parsedCommand) => {
      if (parsedCommand.command !== "credit"
        && parsedCommand.command !== "debit"
        && !(parsedCommand.command === "bbal" && parsedCommand.action === "set")) {
        return;
      }
      const normalizedToken = parsedCommand.account.trim().toLowerCase();
      const existingItem = stagedCatalog.find((item) => (
        item.name.trim().toLowerCase() === normalizedToken
        || item.alias?.trim().toLowerCase() === normalizedToken
      ));
      if (existingItem) return;

      const provisionalItem: TAccountCatalogItem = {
        taccountId: createProvisionalTAccountId(),
        name: parsedCommand.account,
        alias: parsedCommand.command === "credit" || parsedCommand.command === "debit"
          ? parsedCommand.accountNumber
          : undefined,
      };
      provisionalAccounts.push(provisionalItem);
      stagedCatalog.push(provisionalItem);
    });

    const prepared = prepareEntrySubmit(value, stagedCatalog);
    if (!prepared) return false;
    const referencedDraftIds = new Set(prepared.draft.tAccounts.map((account) => account.taccountId));
    const persistedProvisionalAccounts = provisionalAccounts.filter((item) => referencedDraftIds.has(item.taccountId));
    const unusedProvisionalIds = new Set(
      provisionalAccounts.filter((item) => !referencedDraftIds.has(item.taccountId)).map((item) => item.taccountId),
    );
    prepared.draft.catalog = prepared.draft.catalog.filter((item) => !unusedProvisionalIds.has(item.taccountId));

    if (documentId && !isReadOnlyDocument && !prepared.draft.showHelp) {
      const result = await saveDocument({
        document_id: documentId,
        account_id: session?.accountId ?? "00000000-0000-0000-0000-000000000000",
        data: {
          tAccounts: prepared.draft.tAccounts,
          uberCommentsByLetter: prepared.draft.uberCommentsByLetter,
          forceTwoDecimalDisplay: prepared.draft.forceTwoDecimalDisplay,
          documentTitle: prepared.draft.documentTitle,
        },
        new_taccounts: persistedProvisionalAccounts.map((item) => ({
          taccount_id: item.taccountId,
          name: item.name,
          alias: item.alias ?? undefined,
        })),
        anonymous_user_id: user && session?.userId ? session.userId : undefined,
        anonymous_account_id: user && session?.accountId ? session.accountId : undefined,
      });
      if (!result.ok || documentGenerationRef.current !== documentGeneration) return false;
      skipNextDocumentSaveRef.current = true;
    }

    commitEntrySubmit(prepared);
    setCatalogState(prepared.draft.catalog.slice().sort((left, right) => left.name.localeCompare(right.name)));
    pushSnapshot(previousSnapshot);
    return true;
  }, [
    buildSnapshot,
    commitEntrySubmit,
    documentId,
    isReadOnlyDocument,
    prepareEntrySubmit,
    pushSnapshot,
    saveDocument,
    session,
    setCatalogState,
    user,
  ]);

  const handleEntrySubmitWithHistory = useCallback(async (value: string): Promise<boolean> => {
    if (!beginTransition()) {
      showError("Wait for the current command or history change to finish.");
      return false;
    }
    try {
      return await executeEntrySubmitWithHistory(value);
    } finally {
      endTransition();
    }
  }, [beginTransition, endTransition, executeEntrySubmitWithHistory, showError]);

  const startExample = useCallback((entryIndex: number) => {
    const template = exampleEntries[entryIndex];
    if (!template || template.commands.length === 0 || isReadOnlyDocument || !documentId
      || loadedDocumentIdRef.current !== documentId || demoTransitionPendingRef.current) return;

    if (maxTAccountsPerDocument !== null
      && tAccounts.length + template.taccounts > maxTAccountsPerDocument) {
      setExampleSpaceNeeded(template.taccounts);
      return;
    }

    const postingCount = template.commands.flatMap((step) => parseSheetCommandBatch(step.command.replace(/\{([a-z])\}/gu, "$1")))
      .filter((command) => command.command === "debit" || command.command === "credit").length;
    const existingPostingCount = tAccounts.reduce((count, account) => count + account.entries.length, 0);
    if (maxTransactionsPerDocument !== null && existingPostingCount + postingCount > maxTransactionsPerDocument) {
      handleTransactionLimitReached(maxTransactionsPerDocument);
      return;
    }

    let entry: ExampleEntry;
    try {
      entry = bindExampleReferences(template, [
        ...tAccounts.flatMap((account) => account.entries.map((posting) => posting.letter)),
        ...Object.keys(uberCommentsByLetter),
      ]);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to prepare the example.");
      return;
    }

    setActiveExample({
      documentId,
      entry,
      stepIndex: 0,
      baselineSnapshot: buildSnapshot(),
      baselineHistory: captureHistory(),
    });
    setDemoAttentionSequence(0);
    prefillCommand("");
  }, [
    buildSnapshot,
    captureHistory,
    documentId,
    handleTransactionLimitReached,
    isReadOnlyDocument,
    maxTAccountsPerDocument,
    maxTransactionsPerDocument,
    prefillCommand,
    showError,
    tAccounts,
    uberCommentsByLetter,
  ]);

  const startExampleWhenIdle = useCallback((entryIndex: number) => {
    if (!beginTransition()) {
      showError("Wait for the current command or history change to finish before starting an example.");
      return;
    }
    try {
      startExample(entryIndex);
    } finally {
      endTransition();
    }
  }, [beginTransition, endTransition, showError, startExample]);

  const requestDemoAttention = useCallback(() => {
    setDemoAttentionSequence((sequence) => sequence + 1);
  }, []);

  const stopDemoTyping = useCallback(() => {
    if (demoTypingTimerRef.current !== null) {
      clearInterval(demoTypingTimerRef.current);
      demoTypingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!activeExample || activeExample.documentId !== documentId) return;
    const command = activeExample.entry.commands[activeExample.stepIndex]?.command;
    prefillCommand("");
    if (!command) return;
    let characterCount = 0;
    demoTypingTimerRef.current = setInterval(() => {
      characterCount += 1;
      prefillCommand(command.slice(0, characterCount));
      if (characterCount >= command.length) stopDemoTyping();
    }, DEMO_TYPING_DELAY_MS);
    return stopDemoTyping;
  }, [activeExample, documentId, prefillCommand, stopDemoTyping]);

  const handleDemoKey = useCallback(async (key: "Enter" | "Backspace" | "Escape") => {
    if (!activeExample || activeExample.documentId !== currentDocumentIdRef.current
      || demoTransitionPendingRef.current) return;

    const { entry } = activeExample;

    demoTransitionPendingRef.current = true;
    try {
      if (key === "Escape") {
        stopDemoTyping();
        flushSync(() => prefillCommand(entry.commands[activeExample.stepIndex]?.command ?? ""));
        const restored = await restoreSnapshot(activeExample.baselineSnapshot, activeExample.baselineHistory);
        if (restored && activeExample.documentId === currentDocumentIdRef.current) {
          prefillCommand("");
          setActiveExample(null);
        }
        return;
      }

      if (key === "Backspace") {
        if (activeExample.stepIndex === 0) return;

        stopDemoTyping();
        flushSync(() => prefillCommand(entry.commands[activeExample.stepIndex]?.command ?? ""));
        const undone = await undo();
        if (!undone || activeExample.documentId !== currentDocumentIdRef.current) return;

        const previousStepIndex = activeExample.stepIndex - 1;
        prefillCommand("");
        setActiveExample((current) => current
          ? { ...current, stepIndex: previousStepIndex }
          : null);
        return;
      }

      if (activeExample.stepIndex >= entry.commands.length) {
        prefillCommand("");
        setActiveExample(null);
        return;
      }

      stopDemoTyping();
      const command = entry.commands[activeExample.stepIndex].command;
      flushSync(() => prefillCommand(command));
      const commandWasExecuted = await handleEntrySubmitWithHistory(command);
      if (!commandWasExecuted || activeExample.documentId !== currentDocumentIdRef.current) return;

      const nextStepIndex = activeExample.stepIndex + 1;
      prefillCommand("");
      setActiveExample((current) => current
        ? { ...current, stepIndex: nextStepIndex }
        : null);
    } finally {
      demoTransitionPendingRef.current = false;
    }
  }, [activeExample, handleEntrySubmitWithHistory, prefillCommand, restoreSnapshot, stopDemoTyping, undo]);

  const runDemoAction = useCallback((key: "Enter" | "Backspace" | "Escape") => {
    void handleDemoKey(key).catch((error: unknown) => {
      showError(error instanceof Error ? error.message : "Unable to continue the example.");
    });
  }, [handleDemoKey, showError]);

  const isDemoModalOpen = anonymousLimitDialogOpen || planLimitDialogOpen || exampleSpaceNeeded !== null
    || Boolean(taccountEditorState || deletePendingTAccount);

  useEffect(() => {
    if (!activeExample || activeExample.documentId !== documentId || isDemoModalOpen) return;
    const dialog = demoDialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        const dialog = demoDialogRef.current;
        if (!dialog) return;
        const targets = [dialog, ...Array.from(dialog.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"))];
        const index = targets.indexOf(document.activeElement as HTMLDivElement | HTMLButtonElement);
        targets[(index + (event.shiftKey ? targets.length - 1 : 1)) % targets.length].focus();
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " ", "PageUp", "PageDown", "Home", "End"].includes(event.key)) return;
      const isAcceptedKey = !event.repeat
        && !event.altKey
        && !event.ctrlKey
        && !event.metaKey
        && !event.shiftKey
        && (event.key === "Enter" || event.key === "Backspace" || event.key === "Escape");
      const isDemoControlEnter = event.key === "Enter" && event.target instanceof Element
        && Boolean(event.target.closest("[data-demo-controls]"));
      if (isAcceptedKey && isDemoControlEnter) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!isAcceptedKey) {
        if (!isDemoControlEnter) requestDemoAttention();
        return;
      }

      runDemoAction(event.key as "Enter" | "Backspace" | "Escape");
    };

    const handleOutsideInteraction = (event: PointerEvent | MouseEvent) => {
      if (event.target instanceof Node && demoDialogRef.current?.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      requestDemoAttention();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("pointerdown", handleOutsideInteraction, true);
    window.addEventListener("click", handleOutsideInteraction, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("pointerdown", handleOutsideInteraction, true);
      window.removeEventListener("click", handleOutsideInteraction, true);
    };
  }, [activeExample, demoAttentionSequence, documentId, isDemoModalOpen, runDemoAction, requestDemoAttention]);

  const handleAnonymousLimitDialogClose = useCallback(() => {
    setAnonymousLimitDialogOpen(false);
  }, []);

  const handleAnonymousLimitSignUp = useCallback(() => {
    setAnonymousLimitDialogOpen(false);
    window.location.href = "/auth?mode=signup";
  }, []);

  const handlePlanLimitDialogClose = useCallback(() => {
    setPlanLimitDialogOpen(false);
    setPlanLimitMessage("");
  }, []);

  const entryListRows = useMemo<EntryListTableRow[]>(
    () => buildEntryListRows(tAccounts, displayLetterBySequence),
    [displayLetterBySequence, tAccounts],
  );

  useEffect(() => {
    if (!documentId || loadedDocumentIdRef.current === documentId) {
      return;
    }

    let active = true;
    loadedDocumentIdRef.current = null;
    setActiveExample(null);
    prefillCommand("");
    skipNextDocumentSaveRef.current = false;
    hasTrackedEditEventRef.current = false;
    clearHistory();
    setIsReadOnlyDocument(true);

    const accountId = sessionAccountId;

    const loadPersistedDocument = async () => {
      const [documentResult, taccountsResult] = await Promise.all([
        loadDocument(
          {
            document_id: documentId,
            account_id: accountId,
            anonymous_user_id: user && session?.userId ? session.userId : undefined,
            anonymous_account_id: user && session?.accountId ? session.accountId : undefined,
          },
          {
            shouldShowError: (error) =>
              error.code !== "ACCOUNT_NOT_FOUND" &&
              error.code !== "AUTHENTICATION_REQUIRED" &&
              error.code !== "DOCUMENT_NOT_FOUND" &&
              error.code !== "DOCUMENT_ACCESS_DENIED",
          },
        ),
        getTAccounts({
          account_id: accountId,
        }, {
          shouldShowError: (error) => error.code !== "UNAUTHORIZED",
        }),
      ]);
      if (!active || currentDocumentIdRef.current !== documentId) return;

      const catalog = taccountsResult.ok
        ? taccountsResult.data.taccounts.map(mapTAccountRecord)
        : [];
      setCatalogState(catalog);

      if ("error" in documentResult) {
        const loadError = documentResult.error;

        if (loadError.code === "ACCOUNT_NOT_FOUND") {
          clearAnonymousSession();
          window.location.href = "/";
          return;
        }

        if (loadError.code === "AUTHENTICATION_REQUIRED") {
          clearAnonymousSession();
          showError("This account requires authentication. Please log in.");
          return;
        }

        if (loadError.code === "DOCUMENT_NOT_FOUND") {
          showError("Document not found.");
          return;
        }

        if (loadError.code === "DOCUMENT_ACCESS_DENIED") {
          showError("This document is read only. Only the author can modify it.");
          return;
        }

        return;
      }

      if (documentResult.data.account_was_linked) {
        clearAnonymousSession();
      }

      setIsReadOnlyDocument(documentResult.data.is_read_only ?? false);

      const loadedData = documentResult.data.data;
      const loadedAccounts = (loadedData.tAccounts ?? []).map((loadedAccount) => {
        const catalogItem = catalog.find((item) => item.taccountId === loadedAccount.taccountId);
        return {
          ...loadedAccount,
          account: catalogItem?.name ?? loadedAccount.account,
          alias: catalogItem?.alias ?? loadedAccount.alias,
        };
      });
      const loadedComments = loadedData.uberCommentsByLetter ?? {};
      const loadedForceTwoDecimalDisplay = loadedData.forceTwoDecimalDisplay ?? false;
      const loadedDocumentTitle = loadedData.documentTitle ?? "";

      setTAccounts(loadedAccounts);
      setUberCommentsByLetter(loadedComments);
      setForceTwoDecimalDisplay(loadedForceTwoDecimalDisplay);
      setDocumentTitle(loadedDocumentTitle);

      const maxSequence = loadedAccounts
        .flatMap((account) => account.entries)
        .reduce((highestSequence, entry) => Math.max(highestSequence, entry.sequence), 0);
      nextEntrySequenceRef.current = maxSequence + 1;

      const alphabet = "abcdefghijklmnopqrstuvwxyz";
      const maxLetterIndex = loadedAccounts
        .flatMap((account) => account.entries)
        .reduce((highestIndex, entry) => {
          const currentIndex = alphabet.indexOf((entry.letter ?? "").toLowerCase());
          return currentIndex > highestIndex ? currentIndex : highestIndex;
        }, -1);
      nextAutoLetterIndexRef.current = maxLetterIndex + 1;

      loadedDocumentIdRef.current = documentId;
    };

    void loadPersistedDocument();
    return () => { active = false; };
  }, [clearHistory, documentId, getTAccounts, loadDocument, mapTAccountRecord, prefillCommand, session, sessionAccountId, setCatalogState, showError, user]);

  useEffect(() => {
    if (!documentId || executedCommandCount <= 0 || hasTrackedEditEventRef.current) {
      return;
    }

    trackEvent("edit_document", {
      document_id: documentId,
      command_count: executedCommandCount,
    });
    hasTrackedEditEventRef.current = true;
  }, [documentId, executedCommandCount]);

  useEffect(() => {
    if (!executedCommandCount || !documentId || isReadOnlyDocument
      || loadedDocumentIdRef.current !== documentId) {
      return;
    }

    if (skipNextDocumentSaveRef.current) {
      skipNextDocumentSaveRef.current = false;
      return;
    }

    const accountId = session?.accountId ?? "00000000-0000-0000-0000-000000000000";

    const payload: SheetDocumentData = {
      tAccounts: tAccountsRef.current,
      uberCommentsByLetter: uberCommentsByLetterRef.current,
      forceTwoDecimalDisplay: forceTwoDecimalDisplayRef.current,
      documentTitle: documentTitleRef.current,
    };

    const persistDocument = async () => {
      const result = await saveDocument(
        {
          document_id: documentId,
          account_id: accountId,
          data: payload,
          anonymous_user_id: user && session?.userId ? session.userId : undefined,
          anonymous_account_id: user && session?.accountId ? session.accountId : undefined,
        },
        {
          shouldShowError: (error) =>
            error.code !== "ACCOUNT_NOT_FOUND" &&
            error.code !== "AUTHENTICATION_REQUIRED" &&
            error.code !== "DOCUMENT_ACCESS_DENIED" &&
            error.code !== "ANONYMOUS_TACCOUNT_VISUALIZATION_LIMIT_REACHED" &&
            error.code !== "SIGNED_UP_TACCOUNT_VISUALIZATION_LIMIT_REACHED" &&
            error.code !== "ANONYMOUS_TRANSACTION_LIMIT_REACHED" &&
            error.code !== "SIGNED_UP_TRANSACTION_LIMIT_REACHED",
        },
      );

      if ("error" in result) {
        if (result.error.code === "ACCOUNT_NOT_FOUND") {
          clearAnonymousSession();
          return;
        }

        if (result.error.code === "AUTHENTICATION_REQUIRED") {
          clearAnonymousSession();
        }

        if (result.error.code === "DOCUMENT_ACCESS_DENIED") {
          showError("You do not have access to this document, sharing is currently not supported.");
          return;
        }

        if (result.error.code === "ANONYMOUS_TACCOUNT_VISUALIZATION_LIMIT_REACHED") {
          setAnonymousLimitDialogOpen(true);
          return;
        }

        if (result.error.code === "SIGNED_UP_TACCOUNT_VISUALIZATION_LIMIT_REACHED") {
          setPlanLimitMessage(result.error.message);
          setPlanLimitDialogOpen(true);
          return;
        }

        if (result.error.code === "ANONYMOUS_TRANSACTION_LIMIT_REACHED") {
          setAnonymousLimitDialogOpen(true);
          return;
        }

        if (result.error.code === "SIGNED_UP_TRANSACTION_LIMIT_REACHED") {
          setPlanLimitMessage(result.error.message);
          setPlanLimitDialogOpen(true);
          return;
        }

        return;
      }

      if (result.data.account_was_linked) {
        clearAnonymousSession();
      }
    };

    void persistDocument();
  }, [documentId, executedCommandCount, isReadOnlyDocument, saveDocument, session, showError, user]);

  const exportFileBaseName = useMemo(() => getExportBaseFileName(documentTitle), [documentTitle]);

  const closeExportMenu = useCallback(() => {
    setExportMenuAnchorEl(null);
  }, []);

  const handleOpenExportMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  }, []);

  const runWithExportNumberFormatting = useCallback(async <TResult,>(
    action: () => Promise<TResult>,
  ): Promise<TResult> => {
    const exportFormatNumbersWithCommas = userSettings.export.formatNumbersWithCommas;
    if (exportFormatNumbersWithCommas === userSettings.ui.formatNumbersWithCommas) {
      return action();
    }

    setExportFormatNumbersWithCommasOverride(exportFormatNumbersWithCommas);
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });

    try {
      return await action();
    } finally {
      setExportFormatNumbersWithCommasOverride(null);
    }
  }, [userSettings.export.formatNumbersWithCommas, userSettings.ui.formatNumbersWithCommas]);

  const handleExportPNG = useCallback(async () => {
    closeExportMenu();

    try {
      const exportArea = exportAreaRef.current;
      if (!exportArea) {
        throw new Error("Unable to find the sheet work area for export.");
      }

      await runWithExportNumberFormatting(async () => exportSheetAsPNG(exportArea, exportFileBaseName));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error generating PNG";
      showError(errorMessage);
    }
  }, [closeExportMenu, exportFileBaseName, runWithExportNumberFormatting, showError]);

  const handleExportPDF = useCallback(async () => {
    closeExportMenu();

    try {
      const exportArea = exportAreaRef.current;
      if (!exportArea) {
        throw new Error("Unable to find the sheet work area for export.");
      }

      await runWithExportNumberFormatting(async () => exportSheetAsPDF(exportArea, exportFileBaseName));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error generating PDF";
      showError(errorMessage);
    }
  }, [closeExportMenu, exportFileBaseName, runWithExportNumberFormatting, showError]);

  const handleExportXLSX = useCallback(async () => {
    closeExportMenu();

    try {
      const exportAccounts = tAccounts.map((account) => ({
        accountName: account.account,
        entries: buildWidgetEntries(account),
      }));
      const exportEntryRows = entryListRows.map((row) => ({
        displayLetter: row.displayLetter,
        account: row.account,
        debitAmount: row.debitAmount,
        creditAmount: row.creditAmount,
        comment: resolveEntryListComment(row, uberCommentsByLetter),
      }));
      const exportUnbalancedRows = imbalancedRelatedSubtotals.map((subtotal) => ({
        letter: subtotal.letter,
        debitSubtotal: subtotal.debitSubtotal,
        creditSubtotal: subtotal.creditSubtotal,
      }));

      await exportSheetAsXLSX({
        fileBaseName: exportFileBaseName,
        forceTwoDecimalDisplay: userSettings.ui.showDecimals,
        accountsPerRow: userSettings.export.maxTAccountsPerRow,
        entryListSeparateSheet: userSettings.export.entriesSeparateTab,
        formatNumbersWithCommas: userSettings.export.formatNumbersWithCommas,
        tAccounts: exportAccounts,
        entryListRows: exportEntryRows,
        unbalancedRows: exportUnbalancedRows,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error generating XLSX";
      showError(errorMessage);
    }
  }, [buildWidgetEntries, closeExportMenu, entryListRows, exportFileBaseName, imbalancedRelatedSubtotals, showError, tAccounts, uberCommentsByLetter, userSettings.export.entriesSeparateTab, userSettings.export.formatNumbersWithCommas, userSettings.export.maxTAccountsPerRow, userSettings.ui.showDecimals]);

  return (
    <Box
      data-testid="sheet-editor"
      minHeight="100vh"
      width="100%"
      bgcolor={(theme) => (theme.palette.mode === "light" ? "#f4f6f7" : theme.palette.background.default)}
      display="flex"
      flexDirection="column"
      justifyContent="center"
    >
      <Banner noFixed />

      <Box
        id="content"
        sx={{
          height: "calc(100vh - 60px)",
          width: "100vw",
          boxSizing: "border-box",
          p: "clamp(12px, 2.5vw, 28px)",
          pt: "clamp(10px, 2vh, 18px)",
          pb: COMMAND_AUTOCOMPLETE_SAFE_BOTTOM_GAP,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <SheetTopToolbar
          undoTooltipLabel={undoTooltipLabel}
          redoTooltipLabel={redoTooltipLabel}
          canUndo={undoStack.length > 0}
          canRedo={redoStack.length > 0}
          exampleEntries={exampleEntries}
          exampleEntriesDisabled={isReadOnlyDocument || isTransitionPending}
          isDemoMode={Boolean(activeExample)}
          onSelectExample={startExampleWhenIdle}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onOpenAccountsDrawer={() => setIsAccountsDrawerOpen(true)}
          onOpenExportMenu={handleOpenExportMenu}
          exportMenuAnchorEl={exportMenuAnchorEl}
          isExportMenuOpen={isExportMenuOpen}
          onCloseExportMenu={closeExportMenu}
          onExportPNG={() => {
            void handleExportPNG();
          }}
          onExportPDF={() => {
            void handleExportPDF();
          }}
          onExportXLSX={() => {
            void handleExportXLSX();
          }}
        />

        <Box
          ref={exportAreaRef}
          data-sheet-export-root
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            display: "grid",
            gridTemplateColumns: shouldShowRelatedTotalsPanel ? "1fr 200px" : "1fr",
            gridTemplateRows: "auto minmax(120px, 1fr) auto auto auto",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              gridColumn: "1 / 2",
              gridRow: "1 / 2",
              px: 3,
              pt: 1.5,
              pb: 1,
            }}
          >
            <Box
              component="button"
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleTitleEditRequestIfWritable}
              sx={{
                border: 0,
                p: 0,
                px: 0.5,
                py: 0.25,
                m: 0,
                background: "transparent",
                cursor: isReadOnlyDocument ? "default" : "pointer",
                color: "text.primary",
                textAlign: "left",
                width: "fit-content",
                borderRadius: 0.75,
                transition: "background-color 120ms ease-in-out",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  lineHeight: 1.25,
                }}
              >
                {documentTitle.trim() || "Untitled"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              gridColumn: "1 / 2",
              gridRow: "2 / 3",
              minHeight: 0,
            }}
          >
            <Box
              ref={tAccountLayoutRef}
              sx={{
                height: "100%",
                p: 3,
                display: "flex",
                flexWrap: "wrap",
                alignContent: "flex-start",
                alignItems: "flex-start",
                gap: 0.5,
                overflow: "auto",
                ...appScrollbarSx,
                position: "relative",
              }}
            >
              {insertionMarkerStyle ? (
                <Box
                  sx={{
                    position: "absolute",
                    left: insertionMarkerStyle.left,
                    top: insertionMarkerStyle.top,
                    width: "2px",
                    height: insertionMarkerStyle.height,
                    borderRadius: "1px",
                    backgroundColor: "rgba(25, 118, 210, 0.45)",
                    pointerEvents: "none",
                    zIndex: 5,
                  }}
                />
              ) : null}
              {tAccounts.map((entry) => (
                <Box
                  key={entry.taccountId}
                  ref={(node: HTMLDivElement | null) => registerTAccountRef(entry.taccountId, node)}
                  sx={{
                    pointerEvents: draggedAccountId ? "none" : "auto",
                    flex: "0 0 auto",
                    width: "fit-content",
                    minWidth: "max-content",
                  }}
                >
                  {draggedAccountId === entry.taccountId && dragPreview ? (
                    <Box
                      sx={{
                        width: dragPreview.width,
                        height: dragPreview.height,
                        maxWidth: "100%",
                        borderRadius: 1,
                        backgroundColor: "rgba(0, 0, 0, 0.06)",
                        border: "1px dashed rgba(0, 0, 0, 0.2)",
                      }}
                    />
                  ) : (
                    <TAccountWidget
                      account={entry.account}
                      accountNumber={entry.alias}
                      entries={buildWidgetEntries(entry)}
                      highlightedLetter={hoveredImbalancedLetter}
                      forceTwoDecimalDisplay={userSettings.ui.showDecimals}
                      formatNumbersWithCommas={activeFormatNumbersWithCommas}
                      onAmountClick={handleAmountEditRequestIfWritable}
                      onBeginningBalanceClick={handleBeginningBalanceEditRequestIfWritable}
                      onLineItemDragStart={isReadOnlyDocument
                        ? undefined
                        : (payload, event) => handleLineItemDragStart(entry.taccountId, entry.account, payload, event)}
                      onLineItemLabelHover={handleLineItemLabelHover}
                      activeDropColumn={lineItemDropTarget?.accountId === entry.taccountId ? lineItemDropTarget.column : null}
                      dragHandle={isReadOnlyDocument ? undefined : (
                        <Box
                          className="t-account-drag-handle"
                          sx={{
                            position: "absolute",
                            right: -2,
                            top: 2,
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            size="small"
                            onMouseDown={(event) => handleGripMouseDown(entry.taccountId, event)}
                            sx={{
                              cursor: draggedAccountId === entry.taccountId ? "grabbing" : "grab",
                              width: 18,
                              height: 18,
                            }}
                            aria-label={`Reorder ${entry.account}`}
                          >
                            <DragIndicatorIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                    />
                  )}
                </Box>
              ))}
              {dragPreview && draggedAccount ? (
                <Box
                  sx={{
                    position: "fixed",
                    left: dragPreview.cursorX - dragPreview.offsetX,
                    top: dragPreview.cursorY - dragPreview.offsetY,
                    zIndex: 1500,
                    pointerEvents: "none",
                  }}
                >
                  <TAccountWidget
                    account={draggedAccount.account}
                    accountNumber={draggedAccount.alias}
                    entries={buildWidgetEntries(draggedAccount)}
                    highlightedLetter={hoveredImbalancedLetter}
                    forceTwoDecimalDisplay={userSettings.ui.showDecimals}
                    formatNumbersWithCommas={activeFormatNumbersWithCommas}
                    onAmountClick={handleAmountEditRequestIfWritable}
                    onBeginningBalanceClick={handleBeginningBalanceEditRequestIfWritable}
                    onLineItemLabelHover={handleLineItemLabelHover}
                    isDragging
                  />
                </Box>
              ) : null}
              {lineItemDrag ? (
                <Box
                  sx={{
                    position: "fixed",
                    left: lineItemDrag.cursorX - lineItemDrag.offsetX,
                    top: lineItemDrag.cursorY - lineItemDrag.offsetY,
                    zIndex: 1600,
                    pointerEvents: "none",
                    width: lineItemDrag.width,
                    minHeight: lineItemDrag.height,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: "1px solid rgba(0, 0, 0, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.96)",
                    boxShadow: "0 10px 24px rgba(0, 0, 0, 0.18)",
                    transition: "transform 140ms ease, box-shadow 140ms ease",
                    transform: "translateY(-2px) rotate(3deg)",
                    transformOrigin: "top left",
                    animation: "line-item-lift 120ms ease-out",
                    "@keyframes line-item-lift": {
                      from: {
                        transform: "translateY(0) rotate(0deg)",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                      },
                      to: {
                        transform: "translateY(-2px) rotate(3deg)",
                        boxShadow: "0 10px 24px rgba(0, 0, 0, 0.18)",
                      },
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                    {lineItemDrag.displayReference} {formatSignedAmount(lineItemDrag.signedAmount)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>

          {shouldShowRelatedTotalsPanel ? (
            <SheetRelatedTotalsPanel
              subtotals={imbalancedRelatedSubtotals}
              hoveredLetter={hoveredImbalancedLetter}
              onHoverLetterChange={setHoveredImbalancedLetter}
              formatAmount={formatAmount}
            />
          ) : null}

          {tAccounts.length > 0 ? (
            <Box sx={{ gridColumn: "1 / -1", gridRow: "3 / 4" }}>
              <EntryListTable
                entryListRows={entryListRows}
                shouldShowRelatedTotalsPanel={shouldShowRelatedTotalsPanel}
                highlightedLetter={hoveredImbalancedLetter}
                onLineItemLabelHover={handleLineItemLabelHover}
                formatAmount={formatAmount}
                resolveComment={(row) => resolveEntryListComment(row, uberCommentsByLetter)}
                resolveCommentParts={(row) => resolveEntryListCommentParts(row, uberCommentsByLetter)}
                onAmountClick={handleAmountEditRequestIfWritable}
                onBeginningBalanceClick={handleBeginningBalanceEditRequestIfWritable}
                onCommentClick={handleCommentEditRequestIfWritable}
                onTopLevelCommentClick={handleTopLevelCommentRequestIfWritable}
                onEmptyCommentClick={handleNewCommentRequestIfWritable}
              />
            </Box>
          ) : null}

          <Box
            className="export-exclude"
            sx={{
              gridColumn: "1 / -1",
              gridRow: "4 / 5",
              borderTop: "1px solid",
              borderColor: (theme) => (
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.22)
                  : alpha(theme.palette.common.black, 0.12)
              ),
            }}
          />

          <Box className="export-exclude" sx={{ gridColumn: "1 / -1", gridRow: "5 / 6" }}>
            <SheetCommandEntry
              onSubmit={handleEntrySubmitWithHistory}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={undoStack.length > 0}
              canRedo={redoStack.length > 0}
              showUndoRedoControls={false}
              accountSuggestions={accountSuggestions}
              lineItemReferenceSuggestions={lineItemReferenceSuggestions}
              editLineItemReferenceSuggestions={editLineItemReferenceSuggestions}
              commentLineItemReferenceSuggestions={commentLineItemReferenceSuggestions}
              commentReferenceHelpByReference={commentReferenceHelpByReference}
              prefillRequest={commandPrefillRequest ?? undefined}
              placeholder={isReadOnlyDocument ? "This document can only be modified by the author" : undefined}
              readOnly={isReadOnlyDocument || Boolean(activeExample)}
              showDemoCaret={Boolean(activeExample && activeExample.stepIndex < activeExample.entry.commands.length)}
              disableAutocompletePopup={Boolean(activeExample || draggedAccountId || lineItemDrag)}
            />
          </Box>

          {activeExample ? (
            <Box
              data-testid="example-entry-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 20,
                backgroundColor: "rgba(255, 255, 255, 0.01)",
                cursor: "default",
              }}
            >
              {/* Connector retained for possible future use.
              {activeExample.stepIndex < activeExample.entry.commands.length ? (
                <Box
                  data-testid="example-command-connector"
                  sx={{
                    position: "absolute",
                    left: 24,
                    right: "calc(clamp(16px, 3vw, 40px) + min(420px, calc(100% - 32px)))",
                    bottom: 13.6,
                    display: "flex",
                    alignItems: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      flex: "0 0 auto",
                      visibility: "hidden",
                      whiteSpace: "pre",
                      font: "inherit",
                      lineHeight: 1.4,
                    }}
                  >
                    {activeExample.entry.commands[activeExample.stepIndex].command}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      bgcolor: "primary.main",
                      opacity: 0.7,
                    }}
                  />
                </Box>
              ) : null} */}
              <Paper
                key={demoAttentionSequence}
                ref={demoDialogRef}
                tabIndex={0}
                role="dialog"
                aria-label={`${activeExample.entry.title} example`}
                aria-live="polite"
                data-attention-sequence={demoAttentionSequence}
                elevation={8}
                sx={{
                  position: "absolute",
                  bottom: 10,
                  right: "clamp(16px, 3vw, 40px)",
                  width: "min(420px, calc(100% - 32px))",
                  maxHeight: "calc(100% - 20px)",
                  overflow: "auto",
                  p: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  animation: demoAttentionSequence > 0
                    ? "demo-popup-jiggle 280ms ease-in-out"
                    : "none",
                  "@keyframes demo-popup-jiggle": {
                    "0%, 100%": { transform: "translateX(0)" },
                    "20%": { transform: "translateX(-10px)" },
                    "40%": { transform: "translateX(8px)" },
                    "60%": { transform: "translateX(-6px)" },
                    "80%": { transform: "translateX(4px)" },
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {activeExample.entry.title}
                </Typography>
                <Typography variant="body2">
                  {activeExample.stepIndex < activeExample.entry.commands.length
                    ? activeExample.entry.commands[activeExample.stepIndex].text
                    : activeExample.entry.finalText ?? "Example complete."}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 2,
                    mb: 0,
                    pt: 1.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    fontFamily: "monospace",
                    fontSize: "0.78rem",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {activeExample.stepIndex < activeExample.entry.commands.length
                    ? "Press: ENTER     to continue\n       BACKSPACE to go back\n       ESC       to abort example"
                    : "Press: ENTER     to finish the demo\n       BACKSPACE to go back\n       ESC       to abort example"}
                </Box>
                <Box data-demo-controls sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button onClick={() => runDemoAction("Backspace")} disabled={activeExample.stepIndex === 0}>
                    Back
                  </Button>
                  <Button onClick={() => runDemoAction("Enter")}>
                    {activeExample.stepIndex < activeExample.entry.commands.length ? "Next" : "Finish"}
                  </Button>
                  <Button onClick={() => runDemoAction("Escape")}>Cancel</Button>
                </Box>
              </Paper>
            </Box>
          ) : null}
        </Box>
      </Box>

      <SheetAccountsDrawer
        open={isAccountsDrawerOpen}
        onClose={() => setIsAccountsDrawerOpen(false)}
        readOnly={isReadOnlyDocument}
        taccountCatalog={taccountCatalog}
        hoveredCatalogTAccountId={hoveredCatalogTAccountId}
        onHoveredCatalogTAccountIdChange={setHoveredCatalogTAccountId}
        onOpenCreateTAccountDialog={openCreateTAccountDialog}
        onOpenEditTAccountDialog={openEditTAccountDialog}
        onDeleteTAccountRequest={setDeletePendingTAccount}
      />

      <SheetAccountDialog
        open={!!taccountEditorState}
        mode={taccountEditorState?.mode ?? "create"}
        name={taccountEditorState?.name ?? ""}
        alias={taccountEditorState?.alias ?? ""}
        onClose={closeTAccountDialog}
        onNameChange={(value) => setTaccountEditorState((previousState) => (
          previousState
            ? { ...previousState, name: value }
            : previousState
        ))}
        onAliasChange={(value) => setTaccountEditorState((previousState) => (
          previousState
            ? { ...previousState, alias: value }
            : previousState
        ))}
        onSave={() => {
          void handleSaveTAccount();
        }}
      />

      <ConfirmDialog
        open={!!deletePendingTAccount}
        title="Delete account?"
        message={deletePendingTAccount ? `Delete ${deletePendingTAccount.name}?` : ""}
        onCancel={() => setDeletePendingTAccount(null)}
        onConfirm={() => void handleDeleteTAccountConfirmed()}
      />

      <AnonymousLimitDialog
        open={anonymousLimitDialogOpen}
        onClose={handleAnonymousLimitDialogClose}
        onSignUp={handleAnonymousLimitSignUp}
      />

      <LimitReachedDialog
        open={planLimitDialogOpen}
        title="Plan Limit Reached"
        message={planLimitMessage}
        onClose={handlePlanLimitDialogClose}
      />

      <LimitReachedDialog
        open={exampleSpaceNeeded !== null}
        title="Not Enough T-Account Space"
        message={exampleSpaceNeeded === null
          ? ""
          : `You don't have space in this document for the T-Accounts needed for this example (${exampleSpaceNeeded} are needed). To view this example you can either delete some t-accounts with the delete command, clear the document with the clear command, or create a new document.`}
        onClose={() => setExampleSpaceNeeded(null)}
      />
    </Box>
  );
}

export default function Sheet() {
  const isNarrowPortrait = useMediaQuery(NARROW_PORTRAIT_MEDIA_QUERY);

  if (isNarrowPortrait) {
    return (
      <Box
        minHeight="100vh"
        bgcolor={(theme) => (theme.palette.mode === "light" ? "#f4f6f7" : theme.palette.background.default)}
        display="flex"
        flexDirection="column"
      >
        <Banner noFixed />
        <Box
          component="main"
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={3}
          textAlign="center"
        >
          <Typography variant="h6">
            Editing T-Accounts on mobile devices is currently not supported.
          </Typography>
        </Box>
      </Box>
    );
  }

  return <SheetEditor />;
}
